import { useEffect, useMemo, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Receipt, Plus, Download, CheckCircle2, XCircle, RotateCcw, Trash2, FileText, Wallet, Search, Layers } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending:   { label: "غير مدفوعة", bg: "bg-amber-100",   text: "text-amber-800",   ring: "ring-amber-300",   dot: "bg-amber-500"  },
  paid:      { label: "مدفوعة",     bg: "bg-emerald-100", text: "text-emerald-800", ring: "ring-emerald-300", dot: "bg-emerald-500" },
  cancelled: { label: "ملغاة",      bg: "bg-slate-100",   text: "text-slate-700",   ring: "ring-slate-300",   dot: "bg-slate-500"  },
};

const fmt = (n) => (Number(n) || 0).toFixed(3);

function StatusPill({ status }) {
  const s = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-black ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

export default function InvoicesPage() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [children, setChildren] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("");
  const [q, setQ] = useState("");

  const [genOpen, setGenOpen] = useState(false);
  const [genForm, setGenForm] = useState({ month: new Date().toISOString().slice(0, 7), amount: 100 });

  const [singleOpen, setSingleOpen] = useState(false);
  const [singleForm, setSingleForm] = useState({ child_id: "", month: new Date().toISOString().slice(0, 7), amount: 100, notes: "", due_date: "" });

  const [payDialog, setPayDialog] = useState(null); // {invoice, method, notes}

  const load = async () => {
    try {
      const [invR, kidsR] = await Promise.all([api.get("/invoices"), api.get("/children")]);
      setInvoices(invR.data);
      setChildren(kidsR.data);
      if (isAdmin) {
        const s = await api.get("/invoices/summary");
        setSummary(s.data);
      }
    } catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    return invoices.filter((inv) => {
      if (statusFilter !== "all" && inv.status !== statusFilter) return false;
      if (monthFilter && inv.month !== monthFilter) return false;
      if (q && !inv.child_name?.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [invoices, statusFilter, monthFilter, q]);

  const distinctMonths = useMemo(() =>
    [...new Set(invoices.map((i) => i.month))].sort().reverse(),
    [invoices]
  );

  const doGenerate = async () => {
    try {
      const r = await api.post(`/invoices/generate-month?month=${genForm.month}&amount=${genForm.amount || 100}`);
      toast.success(`تم إنشاء ${r.data.created} فاتورة (تم تخطي ${r.data.skipped} موجودة)`);
      setGenOpen(false);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const doCreateSingle = async () => {
    if (!singleForm.child_id) { toast.error("اختر طفلاً"); return; }
    try {
      await api.post("/invoices", singleForm);
      toast.success("تم إنشاء الفاتورة");
      setSingleOpen(false);
      setSingleForm({ child_id: "", month: new Date().toISOString().slice(0, 7), amount: 100, notes: "", due_date: "" });
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const doMarkPaid = async () => {
    try {
      await api.post(`/invoices/${payDialog.invoice.id}/mark-paid`, { method: payDialog.method, notes: payDialog.notes });
      toast.success("تم تسجيل الدفع");
      setPayDialog(null);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const doMarkUnpaid = async (inv) => {
    if (!confirm("إلغاء حالة الدفع؟")) return;
    try { await api.post(`/invoices/${inv.id}/mark-unpaid`); toast.success("تم إعادة الفاتورة إلى غير مدفوعة"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const doCancel = async (inv) => {
    if (!confirm("إلغاء الفاتورة؟")) return;
    try { await api.patch(`/invoices/${inv.id}`, { status: "cancelled" }); toast.success("تم الإلغاء"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const doDelete = async (inv) => {
    if (!confirm("حذف نهائي — تأكيد؟")) return;
    try { await api.delete(`/invoices/${inv.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const downloadPdf = async (inv) => {
    try {
      const r = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `فاتورة-${inv.child_name}-${inv.month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 flex items-center gap-3">
            <Receipt className="w-8 h-8 text-sky-600" />
            الفواتير والمدفوعات
          </h1>
          <p className="text-slate-500 mt-2">
            {isAdmin ? "إصدار فواتير شهرية وتسجيل المدفوعات (العملة: د.ك)" : "فواتير طفلك"}
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setGenOpen(true)} className="h-12 gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="generate-month-btn">
              <Layers className="w-4 h-4" /> إصدار فواتير الشهر
            </Button>
            <Button onClick={() => setSingleOpen(true)} variant="outline" className="h-12 gap-2 text-sky-700 border-sky-200" data-testid="new-invoice-btn">
              <Plus className="w-4 h-4" /> فاتورة يدوية
            </Button>
          </div>
        )}
      </div>

      {isAdmin && summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <SummaryCard label="غير مدفوعة" count={summary.by_status.pending.count} total={summary.by_status.pending.total} currency={summary.currency} tone="amber" testid="sum-pending" />
          <SummaryCard label="مدفوعة"     count={summary.by_status.paid.count}    total={summary.by_status.paid.total}    currency={summary.currency} tone="emerald" testid="sum-paid" />
          <SummaryCard label="ملغاة"      count={summary.by_status.cancelled.count} total={summary.by_status.cancelled.total} currency={summary.currency} tone="slate" testid="sum-cancelled" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 bg-white rounded-xl border border-slate-200 p-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="ابحث باسم الطفل..." value={q} onChange={(e) => setQ(e.target.value)} className="ps-9 h-10" data-testid="invoice-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-10" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الحالات</SelectItem>
            <SelectItem value="pending">غير مدفوعة</SelectItem>
            <SelectItem value="paid">مدفوعة</SelectItem>
            <SelectItem value="cancelled">ملغاة</SelectItem>
          </SelectContent>
        </Select>
        <Select value={monthFilter || "all"} onValueChange={(v) => setMonthFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 h-10" data-testid="filter-month"><SelectValue placeholder="كل الأشهر" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">كل الأشهر</SelectItem>
            {distinctMonths.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {filtered.length === 0 ? (
          <Card><CardContent className="p-16 text-center text-slate-400">لا توجد فواتير مطابقة</CardContent></Card>
        ) : (
          filtered.map((inv) => (
            <Card key={inv.id} className="card-elevated border-slate-200/70" data-testid={`invoice-${inv.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${STATUS_META[inv.status]?.bg}`}>
                      <Receipt className={`w-6 h-6 ${STATUS_META[inv.status]?.text}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-lg font-black text-slate-900">{inv.child_name}</div>
                        <StatusPill status={inv.status} />
                      </div>
                      <div className="text-sm text-slate-500 mt-1">
                        شهر <span className="font-bold text-slate-700">{inv.month}</span>
                        {inv.due_date && <span> · استحقاق: {inv.due_date}</span>}
                      </div>
                      {inv.status === "paid" && inv.paid_at && (
                        <div className="text-xs text-emerald-700 mt-1">
                          دُفعت في {inv.paid_at.slice(0, 10)} · {inv.paid_method === "cash" ? "نقداً" : inv.paid_method}
                        </div>
                      )}
                      {inv.notes && <div className="text-xs text-slate-500 mt-1">{inv.notes}</div>}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-black text-sky-700">{fmt(inv.amount)}</div>
                    <div className="text-[10px] font-bold text-slate-500 uppercase">{inv.currency_label || "د.ك"}</div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-slate-100">
                  <Button variant="outline" size="sm" onClick={() => downloadPdf(inv)} className="gap-1.5 text-sky-700 border-sky-200" data-testid={`invoice-pdf-${inv.id}`}>
                    <Download className="w-3.5 h-3.5" /> PDF
                  </Button>
                  {isAdmin && inv.status === "pending" && (
                    <Button size="sm" onClick={() => setPayDialog({ invoice: inv, method: "cash", notes: "" })} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid={`mark-paid-${inv.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> تسجيل الدفع
                    </Button>
                  )}
                  {isAdmin && inv.status === "paid" && (
                    <Button variant="outline" size="sm" onClick={() => doMarkUnpaid(inv)} className="gap-1.5 text-amber-700 border-amber-200" data-testid={`unpay-${inv.id}`}>
                      <RotateCcw className="w-3.5 h-3.5" /> إلغاء الدفع
                    </Button>
                  )}
                  {isAdmin && inv.status !== "cancelled" && inv.status !== "paid" && (
                    <Button variant="outline" size="sm" onClick={() => doCancel(inv)} className="gap-1.5 text-slate-700 border-slate-200" data-testid={`cancel-${inv.id}`}>
                      <XCircle className="w-3.5 h-3.5" /> إلغاء
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="outline" size="sm" onClick={() => doDelete(inv)} className="gap-1.5 text-rose-700 border-rose-200 hover:bg-rose-50" data-testid={`delete-invoice-${inv.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> حذف
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Generate month dialog */}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent data-testid="generate-dialog">
          <DialogHeader><DialogTitle className="text-2xl font-black">إصدار فواتير الشهر</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 rounded-lg bg-sky-50 border border-sky-200 text-sm text-sky-900">
              سيتم إنشاء فاتورة لكل طفل مسجّل (لن يتم تكرار الفواتير الموجودة).
            </div>
            <div className="space-y-1.5">
              <Label className="font-bold">الشهر (YYYY-MM)</Label>
              <Input type="month" value={genForm.month} onChange={(e) => setGenForm({ ...genForm, month: e.target.value })} data-testid="gen-month" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-bold">قيمة الرسم الشهري (د.ك)</Label>
              <Input type="number" step="0.001" value={genForm.amount} onChange={(e) => setGenForm({ ...genForm, amount: parseFloat(e.target.value || 0) })} data-testid="gen-amount" />
            </div>
            <Button onClick={doGenerate} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="confirm-generate">
              <Layers className="w-4 h-4" /> إنشاء الفواتير
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Single invoice dialog */}
      <Dialog open={singleOpen} onOpenChange={setSingleOpen}>
        <DialogContent data-testid="single-dialog">
          <DialogHeader><DialogTitle className="text-2xl font-black">فاتورة جديدة</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label className="font-bold">الطفل</Label>
              <Select value={singleForm.child_id} onValueChange={(v) => setSingleForm({ ...singleForm, child_id: v })}>
                <SelectTrigger data-testid="sf-child"><SelectValue placeholder="اختر طفلاً" /></SelectTrigger>
                <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-bold">الشهر</Label>
                <Input type="month" value={singleForm.month} onChange={(e) => setSingleForm({ ...singleForm, month: e.target.value })} data-testid="sf-month" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold">المبلغ (د.ك)</Label>
                <Input type="number" step="0.001" value={singleForm.amount} onChange={(e) => setSingleForm({ ...singleForm, amount: parseFloat(e.target.value || 0) })} data-testid="sf-amount" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="font-bold">تاريخ الاستحقاق (اختياري)</Label>
              <Input type="date" value={singleForm.due_date} onChange={(e) => setSingleForm({ ...singleForm, due_date: e.target.value })} data-testid="sf-due" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-bold">ملاحظات</Label>
              <Textarea rows={2} value={singleForm.notes} onChange={(e) => setSingleForm({ ...singleForm, notes: e.target.value })} data-testid="sf-notes" />
            </div>
            <Button onClick={doCreateSingle} className="w-full h-12 bg-sky-600 hover:bg-sky-700 gap-2" data-testid="confirm-single">
              <Plus className="w-4 h-4" /> إنشاء
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pay dialog */}
      <Dialog open={!!payDialog} onOpenChange={(v) => !v && setPayDialog(null)}>
        {payDialog && (
          <DialogContent data-testid="pay-dialog">
            <DialogHeader><DialogTitle className="text-2xl font-black">تسجيل دفع فاتورة</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
                فاتورة <b>{payDialog.invoice.child_name}</b> · شهر {payDialog.invoice.month} · {fmt(payDialog.invoice.amount)} د.ك
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold">طريقة الدفع</Label>
                <Select value={payDialog.method} onValueChange={(v) => setPayDialog({ ...payDialog, method: v })}>
                  <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">نقداً</SelectItem>
                    <SelectItem value="knet">كي نت</SelectItem>
                    <SelectItem value="bank_transfer">تحويل بنكي</SelectItem>
                    <SelectItem value="cheque">شيك</SelectItem>
                    <SelectItem value="other">أخرى</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold">ملاحظات</Label>
                <Textarea rows={2} value={payDialog.notes} onChange={(e) => setPayDialog({ ...payDialog, notes: e.target.value })} data-testid="pay-notes" />
              </div>
              <Button onClick={doMarkPaid} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="confirm-pay">
                <CheckCircle2 className="w-4 h-4" /> تأكيد الدفع
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}

function SummaryCard({ label, count, total, currency, tone, testid }) {
  const tones = {
    amber:   { bg: "from-amber-50 to-amber-100/50",   ring: "border-amber-200",   text: "text-amber-700",   icon: Wallet },
    emerald: { bg: "from-emerald-50 to-emerald-100/50", ring: "border-emerald-200", text: "text-emerald-700", icon: CheckCircle2 },
    slate:   { bg: "from-slate-50 to-slate-100/50",   ring: "border-slate-200",   text: "text-slate-700",   icon: XCircle },
  }[tone] || {};
  const Icon = tones.icon || Wallet;
  return (
    <Card className={`bg-gradient-to-br ${tones.bg} border ${tones.ring}`} data-testid={testid}>
      <CardContent className="p-5 flex items-center justify-between">
        <div>
          <div className={`text-xs font-black uppercase tracking-wider ${tones.text}`}>{label}</div>
          <div className="text-3xl font-black text-slate-900 mt-1">{count}</div>
          <div className={`text-sm font-bold mt-1 ${tones.text}`}>{fmt(total)} {currency}</div>
        </div>
        <div className={`w-12 h-12 rounded-2xl bg-white/60 flex items-center justify-center ${tones.text}`}>
          <Icon className="w-6 h-6" />
        </div>
      </CardContent>
    </Card>
  );
}
